import { ErrorBoundary } from "@norkron/web-error-boundary";
import {
  ContractViolationError,
  PrivilegesAdminPage,
  PrivilegesAdminProvider,
  type PrivilegesAdminDeps,
} from "@norkron/web-privileges-admin";
import { privzBff } from "../../api/privzBff";
import { Button } from "../../compose/controls";
import { EmptyState, ErrorState } from "../../compose/states";
import { isSurfaceNotServed, readableError } from "../../lib/errorCopy";
import { PRIVZ_VOCABULARY } from "../../lib/privzVocabulary";

/**
 * Access administration — groups, multi-group membership, role bundles, grants
 * with scope + ceiling pre-check, elevation, access-review campaigns and
 * separation-of-duties, at `/w/:slug/admin`.
 *
 * ## Composed, not assembled
 *
 * This file is a PROVIDER and a PAGE. `web-privileges-admin` ships eleven
 * components and the whole read/write client; elaborate contributes a transport
 * (`api/privzBff.ts`), some display copy (`lib/privzVocabulary.ts`) and the scope
 * the console operates in. There is no elaborate-side privileges logic and there
 * must not be.
 *
 * ⛔ `PrivilegesAdminProvider` + `PrivilegesAdminPage` DIRECTLY, never
 * `fillPrivilegesAdminSeat` / `createPrivilegesAdminModule` / `buildConsole`.
 * Those three exist to slot the module into `@norkron/web-console-shell`, which
 * elaborate does not and should not adopt: its `NavTree` renders a raw `href` —
 * a full document load that kills the SPA — and it carries a SCAFFOLD-STAGE
 * banner. Every `web-console-shell` reference inside `web-privileges-admin` is
 * `import type` only and its built `dist` has no runtime import of it, so wrapping
 * the subtree by hand is the package's own documented alternative, not a hack.
 *
 * ## What is deliberately NOT passed
 *
 * `snapshotSource` is omitted. Since privz v0.8.0 the mount serves
 * `GET /v1/privileges/snapshot`, and `PrivilegesAdminProvider` defaults to it
 * through this same transport — so the multi-group provenance panels work with
 * zero host wiring. Supplying one would be for a host with its OWN pre-scoped
 * snapshot route, which elaborate does not have.
 *
 * ⛔ And elaborate must never ASSEMBLE one. A `PolicySnapshot` is server output:
 * subject visit order alone is load-bearing in the resolver, so a close-but-wrong
 * snapshot means being confidently wrong about someone's access — the exact
 * failure mode the anchor model's `anchor_state` warning describes, applied to
 * authorization.
 *
 * ## Authority
 *
 * ⛔ Nothing here decides anything. The WASM resolver this console loads exists to
 * EXPLAIN a provenance and PREVIEW a change; every request is re-resolved and
 * enforced server-side, and `assertNoVerdict` in the transport refuses any payload
 * that tries to carry a client-computed answer.
 *
 * ⚠️ HONEST STATE: this mounts the console against elaborate's `/api` transport.
 * `elaborate-app` does NOT mount the privz route family — there is no
 * `/v1/privileges/*` route in the public binary at all — so every one of the
 * console's five required first reads answers `404`.
 *
 * ## What that used to look like, and why it was wrong TWICE
 *
 * `PrivilegesAdminPage` re-throws its load error from the COMPONENT BODY, one line
 * above the `ViewContainer` whose `fallback` prop carries the lib's own good copy.
 * A boundary cannot catch a throw from the component that renders it, so the error
 * escaped past the lib's fallback into elaborate's `TabPanel` boundary, which
 * titled it `Panel "access" failed to render` over the message
 * `An unexpected error occurred.` (the message was EMPTY — see `lib/errorCopy`).
 *
 * Wrong twice: the failure is not unexpected — it is the exactly-predicted
 * consequence of a route family nobody has mounted yet — and a panel that cannot
 * load must not take its tab down with it.
 *
 * {@link AccessAdmin} therefore CLASSIFIES the throw rather than letting it fly:
 *
 *   · `404`/`501` on the mount → **not a fault.** A calm, non-error notice that
 *     access management is not available in this workspace yet. No red, no
 *     warning glyph, no retry that cannot succeed.
 *   · anything else → a genuine error state, with a retry, in plain words.
 *
 * ⛔ WHAT THIS DOES NOT DO. It does not fake data, does not stub the transport and
 * does not soften the console's refusal to answer an unvalidatable response —
 * `assertNoVerdict`, the `CONSOLE_PROFILE` route lookup and the schema parse are
 * all untouched. A refusal is still a refusal; it is now readable.
 */

export function AccessAdmin({
  accountId,
  workspaceSlug,
}: {
  /** The account whose groups and bundles this console administers. */
  accountId: string;
  /** The workspace the resolutions are performed against. */
  workspaceSlug: string;
}) {
  // `scope_kind` / `scope_id` are the server's own scope selector shape. The
  // workspace slug is elaborate's tenant unit (CLAUDE.md: "tenant unit =
  // workspace"), so it is the only honest scope id to resolve against.
  const deps: PrivilegesAdminDeps = {
    client: privzBff,
    accountId,
    scope: { scope_kind: "workspace", scope_id: workspaceSlug },
    vocabulary: PRIVZ_VOCABULARY,
  };

  return (
    <div className="admin-access" data-testid="admin-access">
      <ErrorBoundary
        // Keyed on the pair the console is scoped to, so switching workspace
        // retries instead of staying in a boundary that failed for the last one.
        resetKeys={[accountId, workspaceSlug]}
        fallback={(error, reset) => accessFallback(error, reset)}
      >
        <PrivilegesAdminProvider deps={deps}>
          <PrivilegesAdminPage />
        </PrivilegesAdminProvider>
      </ErrorBoundary>
    </div>
  );
}

/**
 * The three — and only three — things a client may be told when the console
 * cannot load, split on TWO questions: did the server say "no such surface", and
 * could we trust what it did say?
 *
 * EXPORTED so it can be asserted directly. Mounting the whole console to check a
 * sentence means booting a WASM resolver and five network reads to test a string;
 * the classification is a pure function of the error, and testing it as one is
 * both faster and not subject to the React-mount fragility the rest of this
 * suite already fights.
 */
export function accessFallback(error: Error, reset: () => void) {
  // ⛔ THE REFUSAL, PRESERVED. When the server's answer does not match the
  // contract the package publishes, the console refuses to draw a HALF-ANSWER —
  // this page is about who can do what, and a plausible-but-wrong permission is
  // worse than none. That behaviour is deliberate and is NOT softened here: the
  // console still renders nothing. What changed is only the sentence, because the
  // package's own words ("does not match the published contract", "refuses to
  // render a partial answer") describe a schema to an engineer, and the reader is
  // a client. The FACT — we could not trust this, so we are showing you nothing
  // rather than something wrong — survives intact.
  if (error instanceof ContractViolationError) {
    return (
      <div data-testid="admin-access-untrusted">
        <ErrorState
          title="We couldn't show access management safely"
          body={
            "The information we got back wasn't what this app expects, so nothing " +
            "is shown rather than something that might be wrong. Nobody's access " +
            "has changed. Please report this — it needs a look."
          }
          action={
            <Button variant="secondary" onClick={reset}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }
  if (isSurfaceNotServed(error)) {
    return (
      // ⛔ `EmptyState`, not `ErrorState`: no `role="alert"`, no warning glyph, no
      // red. "Not switched on yet" is a FACT about this workspace, and dressing a
      // fact as a failure is what made a client think something was broken.
      <div data-testid="admin-access-unavailable">
        <EmptyState
          title="Access management isn't available yet"
          body={
            "Groups, role bundles and access reviews aren't switched on for this " +
            "workspace. Everyone's existing access is unaffected — Users and " +
            "Permissions are where access is managed today."
          }
        />
      </div>
    );
  }
  return (
    <div data-testid="admin-access-error">
      <ErrorState
        title="We couldn't load access management"
        body={readableError(error, "Please try again in a moment.")}
        action={
          <Button variant="secondary" onClick={reset}>
            Try again
          </Button>
        }
      />
    </div>
  );
}
